import React, { useState } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { Trash2, AlertTriangle, Lock } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Floating bulk-delete action bar + confirmation modal + PIN prompt.
 * Accepts `selectedIds` (array) and fires onDone() after a successful delete.
 *
 * Usage:
 *   <BulkDeleteBar
 *     section="bookings"
 *     selectedIds={selected}
 *     onClearSelection={() => setSelected([])}
 *     onDone={() => { refresh(); setSelected([]); }}
 *     labelSingular="booking"
 *   />
 */
export const BulkDeleteBar = ({
  section,
  selectedIds,
  onClearSelection,
  onDone,
  labelSingular = 'record',
  dataTestPrefix = 'bulk',
}) => {
  const [open, setOpen] = useState(false);
  const [pin, setPin] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const count = selectedIds?.length || 0;
  if (count === 0) return null;

  const plural = count === 1 ? labelSingular : `${labelSingular}s`;

  const submit = async () => {
    if (!pin.trim()) { setError('Enter your PIN or admin password'); return; }
    setSubmitting(true); setError('');
    try {
      const res = await axios.post(
        `${API_URL}/api/admin/bulk-delete`,
        { section, ids: selectedIds, pin: pin.trim() },
        { withCredentials: true }
      );
      const cascaded = Object.values(res.data.cascade || {}).reduce((a, b) => a + b, 0);
      toast.success(
        `Deleted ${res.data.deleted} ${plural}` + (cascaded > 0 ? ` + ${cascaded} linked records` : '')
      );
      setOpen(false); setPin('');
      onClearSelection?.();
      onDone?.(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Delete failed');
    } finally { setSubmitting(false); }
  };

  return (
    <>
      {/* Sticky floating bar */}
      <div
        className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-white rounded-full shadow-xl border border-[#E8E5DF] px-4 py-2 flex items-center gap-3"
        data-testid={`${dataTestPrefix}-floating-bar`}
      >
        <span className="text-sm text-[#1E1E24] font-medium">
          {count} {plural} selected
        </span>
        <button
          onClick={onClearSelection}
          className="text-xs text-[#6B6B7A] hover:text-[#1E1E24] underline"
          data-testid={`${dataTestPrefix}-clear`}
        >
          Clear
        </button>
        <Button
          size="sm"
          onClick={() => setOpen(true)}
          className="rounded-full bg-[#FF3B6B] hover:bg-[#E62E58] text-white"
          data-testid={`${dataTestPrefix}-open-dialog`}
        >
          <Trash2 className="w-4 h-4 mr-1" /> Delete Selected
        </Button>
      </div>

      {/* Confirmation + PIN dialog */}
      <Dialog open={open} onOpenChange={(o) => { if (!submitting) { setOpen(o); if (!o) { setPin(''); setError(''); } } }}>
        <DialogContent className="bg-white max-w-md" data-testid={`${dataTestPrefix}-dialog`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Fredoka, sans-serif' }}>
              <AlertTriangle className="w-5 h-5 text-[#FF3B6B]" />
              Bulk Delete Confirmation
            </DialogTitle>
            <DialogDescription>
              You're about to permanently remove <strong>{count} {plural}</strong> and any linked data
              (bookings cascade to forms + invoices; customers cascade to their bookings; employees to shifts + messages).
            </DialogDescription>
          </DialogHeader>

          <div className="rounded-xl bg-[#FFF4E6] border border-[#FFD166] p-3 text-sm text-[#9C6B12]">
            This action is logged in the Audit Log. Records are soft-deleted and hidden from all views.
          </div>

          <div>
            <label className="block text-sm font-medium text-[#1E1E24] mb-1" htmlFor="bulk-pin">
              <Lock className="w-3.5 h-3.5 inline-block mr-1" />
              Confirm with your PIN or admin password
            </label>
            <Input
              id="bulk-pin"
              type="password"
              inputMode="numeric"
              value={pin}
              onChange={(e) => { setPin(e.target.value); setError(''); }}
              placeholder="4-digit PIN or password"
              className="rounded-xl border-[#E8E5DF]"
              autoFocus
              data-testid={`${dataTestPrefix}-pin-input`}
            />
            {error && <p className="text-xs text-[#FF3B6B] mt-1" data-testid={`${dataTestPrefix}-error`}>{error}</p>}
          </div>

          <DialogFooter>
            <Button
              variant="outline" onClick={() => { setOpen(false); setPin(''); setError(''); }}
              disabled={submitting} className="rounded-full"
              data-testid={`${dataTestPrefix}-cancel`}
            >
              Cancel
            </Button>
            <Button
              onClick={submit} disabled={submitting || !pin.trim()}
              className="rounded-full bg-[#FF3B6B] hover:bg-[#E62E58] text-white"
              data-testid={`${dataTestPrefix}-confirm`}
            >
              <Trash2 className="w-4 h-4 mr-1" />
              {submitting ? 'Deleting…' : 'Permanently Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

/** Reusable select-all header checkbox + per-row checkbox helper.
 *  <BulkSelectCheckbox id={row.id} selected={selected} setSelected={setSelected} />
 *  <BulkSelectAllCheckbox ids={allIds} selected={selected} setSelected={setSelected} />
 */

export const BulkSelectCheckbox = ({ id, selected, setSelected, testid }) => {
  const checked = selected.includes(id);
  const toggle = (e) => {
    e.stopPropagation();
    setSelected(checked ? selected.filter((x) => x !== id) : [...selected, id]);
  };
  return (
    <input
      type="checkbox"
      checked={checked}
      onChange={toggle}
      onClick={(e) => e.stopPropagation()}
      className="w-4 h-4 accent-[#FF3B6B] cursor-pointer"
      data-testid={testid || `bulk-checkbox-${id}`}
    />
  );
};

export const BulkSelectAllCheckbox = ({ ids, selected, setSelected, testid = 'bulk-checkbox-all' }) => {
  const allSelected = ids.length > 0 && ids.every((i) => selected.includes(i));
  const someSelected = !allSelected && ids.some((i) => selected.includes(i));
  const toggle = () => {
    if (allSelected) setSelected(selected.filter((x) => !ids.includes(x)));
    else setSelected(Array.from(new Set([...selected, ...ids])));
  };
  return (
    <input
      type="checkbox"
      ref={(el) => { if (el) el.indeterminate = someSelected; }}
      checked={allSelected}
      onChange={toggle}
      className="w-4 h-4 accent-[#FF3B6B] cursor-pointer"
      data-testid={testid}
    />
  );
};

export default BulkDeleteBar;
